import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppData } from "../context/AppDataContext";
import { filterToursForPublic } from "../constants/publicTours";
import { getTourCardCopy } from "../utils/tourCardCopy";

const STORAGE_KEY = "travelina_entry_popup_shown";
const FEATURED_TOUR_KEYS = [
  "alicante-maj",
  "madrid-segovia",
  "valladolid-1tjedan",
  "valladolid-2tjedan",
];

export default function EntryPopup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { tours } = useAppData();
  const [visible, setVisible] = useState(false);

  const base = (i18n.language || "hr").slice(0, 2);

  const featuredTours = useMemo(() => {
    const publicTours = filterToursForPublic(tours || []);
    const byKey = new Map(publicTours.map((tour) => [tour.key, tour]));
    const prioritized = FEATURED_TOUR_KEYS.map((key) => byKey.get(key)).filter(Boolean);

    // Fallback ako neki ključ nije dostupan u podacima.
    if (prioritized.length === 0) {
      return [...publicTours]
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 4);
    }

    return prioritized;
  }, [tours]);

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem(STORAGE_KEY);
    if (alreadyShown) return;
    const timer = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const close = () => {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setVisible(false);
      document.body.style.overflow = "";
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [visible]);

  const handleClose = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
    document.body.style.overflow = "";
  };

  const scrollToTours = () => {
    handleClose();
    const el = document.getElementById("tours");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goToTour = (key) => {
    handleClose();
    navigate(`/tour/${key}`);
  };

  if (!visible) return null;

  const useLiveTours = featuredTours.length > 0;

  return (
    <div
      className="tour-popup-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="entry-popup-title"
    >
      <div className="tour-popup entry-popup" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="tour-popup-close"
          onClick={handleClose}
          aria-label={t("tourPopup.close") || "Zatvori"}
        >
          ×
        </button>

        <div className="entry-popup-content">
          <h2 id="entry-popup-title" className="entry-popup-title">
            {t("entryPopup.title") || "Dobrodošli u TraveLinu"}
          </h2>
          <p className="entry-popup-text">
            {useLiveTours
              ? t("entryPopup.subtitleDynamic")
              : t("entryPopup.subtitle") ||
                "Pogledajte naša putovanja – Valladolid s učenjem španjolskog te Alicante, proljetni bijeg na Costa Blancu."}
          </p>

          {useLiveTours ? (
            <div className="entry-popup-tours" role="list">
              {featuredTours.map((tour) => {
                const { title, desc } = getTourCardCopy(tour, base, t);
                const short =
                  desc.length > 120 ? `${desc.slice(0, 120).trim()}…` : desc;
                return (
                  <button
                    key={tour.id}
                    type="button"
                    className="entry-popup-tour-row"
                    onClick={() => goToTour(tour.key)}
                    role="listitem"
                  >
                    <span
                      className="entry-popup-tour-thumb"
                      style={
                        tour.image
                          ? { backgroundImage: `url(${tour.image})` }
                          : undefined
                      }
                      aria-hidden
                    />
                    <span className="entry-popup-tour-text">
                      {tour.tag ? (
                        <span className="entry-popup-badge entry-popup-badge-new">
                          {tour.tag}
                        </span>
                      ) : null}
                      <span className="entry-popup-tour-title">{title}</span>
                      <span className="entry-popup-tour-desc">{short}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="entry-popup-highlights">
              <div className="entry-popup-highlight">
                <span className="entry-popup-badge">Valladolid</span>
                <span>{t("entryPopup.valladolid") || "Španjolski jezik, Madrid, Salamanca, Segovia"}</span>
              </div>
              <div className="entry-popup-highlight">
                <span className="entry-popup-badge entry-popup-badge-new">Alicante</span>
                <span>{t("entryPopup.alicante") || "Proljetni bijeg – vino, more, radionica paelle"}</span>
              </div>
            </div>
          )}

          <div className="tour-popup-actions">
            <button type="button" className="btn primary" onClick={scrollToTours}>
              {t("entryPopup.cta") || "Pogledaj putovanja"}
            </button>
            <button type="button" className="btn ghost" onClick={handleClose}>
              {t("tourPopup.close") || "Zatvori"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
